import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { CellProps } from 'react-table'
import {
  CustomTable,
  InfoAlert,
  PlainHeader,
  StackedCell,
  TableColumn,
  TableEmptyState,
} from '../../../../components'
import { RelatorioAnexoIi } from '../core/_models'
import { getAnexoIi } from '../core/_requests'
import { formatarContagem } from '../core/filtro'
import { useParametrosRelatorios } from '../core/ListFiltersProvider'
import {
  RelatorioMensalExpandedRow,
  COLUNA_ANEXO_II,
  LinhaAnexo,
  valorCelulaAnexoIi,
} from './RelatorioMensalExpandedRow'

/** A prévia mostra só o começo do mês; o relatório completo é o arquivo baixado. */
const LINHAS_PREVIA = 100

/** Pendências de cadastro que deixam linhas do relatório mensal incompletas. */
const pendenciasDoDado = (relatorio: RelatorioAnexoIi) =>
  [
    relatorio.semLotacaoSolicitante > 0 &&
      `${formatarContagem(relatorio.semLotacaoSolicitante)} sem lotação do solicitante`,
    relatorio.semCbo > 0 && `${formatarContagem(relatorio.semCbo)} sem CBO do solicitante`,
    relatorio.semCid > 0 && `${formatarContagem(relatorio.semCid)} sem CID`,
  ].filter((texto): texto is string => Boolean(texto))

// Resumo na linha; as 17 colunas do edital ficam na linha expandida.
const resumo = (titulo: string, coluna: number, cellWidth?: string): TableColumn<LinhaAnexo> => ({
  Header: () => <PlainHeader>{titulo}</PlainHeader>,
  id: `coluna-${coluna}`,
  cellWidth,
  Cell: ({ row }: CellProps<LinhaAnexo>) => (
    <StackedCell primary={valorCelulaAnexoIi(coluna, row.original.celulas[coluna])} />
  ),
})

const columns: ReadonlyArray<TableColumn<LinhaAnexo>> = [
  // Coluna do botão de expandir (o ModernTable desenha o botão na coluna de id `expand`).
  {
    Header: () => <span className='visually-hidden'>Detalhes</span>,
    id: 'expand',
    cellWidth: '56px',
  },
  resumo('Identificador', COLUNA_ANEXO_II.identificador, '220px'),
  resumo('Especialidade', COLUNA_ANEXO_II.especialidade),
  resumo('Solicitação', COLUNA_ANEXO_II.solicitacao, '180px'),
  resumo('Situação', COLUNA_ANEXO_II.situacao, '180px'),
]

/**
 * Relatório mensal do Anexo II (17 colunas fixas, vindas do `cabecalho`). A prévia em JSON
 * mostra os alertas de qualidade do dado antes do envio à Secretaria de Saúde; CSV e XLSX são os arquivos de
 * envio (toolbar).
 */
export const RelatorioMensalTable = () => {
  const { anexoIi } = useParametrosRelatorios()

  const { data, isFetching, isError, refetch } = useQuery(
    ['interconsulta-relatorio-anexo-ii', anexoIi],
    () => getAnexoIi(anexoIi!),
    { enabled: !!anexoIi, refetchOnWindowFocus: false, retry: false }
  )

  const cabecalho = useMemo(() => data?.cabecalho ?? [], [data?.cabecalho])

  const linhas = useMemo<LinhaAnexo[]>(
    () =>
      (anexoIi ? data?.linhas ?? [] : [])
        .slice(0, LINHAS_PREVIA)
        .map((celulas, id) => ({ id, celulas })),
    [anexoIi, data?.linhas]
  )

  const pendencias = data ? pendenciasDoDado(data) : []

  return (
    <>
      {anexoIi && data && (
        <div className='px-9 pt-2'>
          <p className='text-gray-700 mb-4' aria-live='polite'>
            {formatarContagem(data.totalLinhas)} linhas na competência
            {data.codigoSes ? ` · Núcleo ${data.codigoSes}` : ''}
          </p>
          {!data.codigoSes && (
            <InfoAlert variant='warning' className='align-items-center'>
              A empresa não tem <strong>Código do Núcleo</strong> no cadastro: a coluna do Núcleo
              sai vazia no relatório.
            </InfoAlert>
          )}
          {pendencias.length > 0 && (
            <InfoAlert
              variant='warning'
              title='Revise antes de enviar'
              className='align-items-center'
            >
              {pendencias.join(' · ')}. Essas linhas saem incompletas no relatório mensal.
            </InfoAlert>
          )}
        </div>
      )}
      <CustomTable<LinhaAnexo>
        id='kt_table_relatorio_ic_anexo_ii'
        ariaLabel='Prévia do relatório mensal do Anexo II'
        data={linhas}
        columns={columns}
        isLoading={isFetching}
        isError={isError}
        onRetry={refetch}
        notPaginated
        enableExpand
        onExpandFetch={(row) => Promise.resolve(row.original)}
        getExpandLabel={(linha) =>
          `teleconsultoria ${linha.celulas[COLUNA_ANEXO_II.identificador] || linha.id + 1}`
        }
        renderExpand={(linha?: LinhaAnexo) => (
          <RelatorioMensalExpandedRow data={linha} cabecalho={cabecalho} />
        )}
        emptyState={
          anexoIi ? (
            <TableEmptyState
              title='Nenhuma interconsulta na competência'
              description='Revise a competência e as unidades escolhidas.'
            />
          ) : (
            <TableEmptyState
              title='Escolha a empresa e a competência'
              description='O relatório mensal aparece com a empresa e o mês de referência.'
            />
          )
        }
      />
      {anexoIi && data && data.linhas.length > LINHAS_PREVIA && (
        <p className='text-gray-700 fs-7 px-9 pb-6 mb-0'>
          Prévia das {formatarContagem(LINHAS_PREVIA)} primeiras de{' '}
          {formatarContagem(data.linhas.length)} linhas. Baixe o CSV ou o XLSX para o relatório
          completo.
        </p>
      )}
    </>
  )
}
